import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';
import { useAuth } from '@sopranodesign/pulse-auth';

import { fetchConfluencePages } from '../api';
import { StateLevel } from '.';

const ConfluencePagesList = ({
  spaceId,
  handleHide,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canEditResource = hasPermission('edit generative ai resource');

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const order = useOrderableTableReducer({ id: `confluence-${spaceId}` });
  const pagination = usePaginationReducer({ id: `confluence-${spaceId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/confluence/pages',
      spaceId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchConfluencePages(spaceId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const pages = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const spaceName = response.space_name ?? 'Pages';

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  return (
    <AnimatePresence>
      <PanelSlider header={spaceName} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !pages.length && (
          <Paragraph mb secondary>No pages available.</Paragraph>
        )}
        {!isLoading && pages.length > 0 && (
          <Flex col>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      { label: 'Title', sort: 'title' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canEditResource ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {pages.map(page => (
                    <TableRow key={page.id}>
                      <TableCell>{page.title}</TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={page.status} />
                          <span>{page.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{page.updated_at}</TableCell>

                      {canEditResource && (
                        <TableActions>
                          <Button
                            icon="download"
                            loading={Boolean(loadingIds?.[page.id])}
                            onClick={() => onOpenRecrawlModal(page)}
                          >
                            Recrawl
                          </Button>

                          {errorIds?.[page.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[page.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

ConfluencePagesList.propTypes = {
  spaceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object
};

export default ConfluencePagesList;
